import type { Workspace } from '../../../types/workspace'
import { normalizeChecklistBlockedTodoPolicy } from '../../../utils/checklistBlockedTodoPolicy'
import { isWorkspaceSubscriptionRestricted } from '../../../utils/subscriptionStatus'
import { isModuleEnabled } from '../../../utils/workspaceModules'

export interface AiChecklistCapabilityContext {
  plan: Workspace['plan']
  subscriptionStatus: Workspace['subscription']['status'] | 'UNKNOWN'
  subscriptionRestricted: boolean
  attachmentContextLimit: number
  attachmentIntelligenceAvailable: boolean
  workspaceCurrency: string
  cardBackgroundsEnabled: boolean
  stockModuleEnabled: boolean
  budgetModuleEnabled: boolean
  quotaModuleEnabled: boolean
  helperVisibilityDefaultsEnabled: boolean
  blockedTodoPolicy: ReturnType<typeof normalizeChecklistBlockedTodoPolicy>
}

type CreateAiChecklistCapabilityContextInput = {
  workspace?: Workspace | null
  attachmentContextLimit?: number
}

const formatBool = (value: boolean) => (value ? 'yes' : 'no')

export const createAiChecklistCapabilityContext = ({
  workspace,
  attachmentContextLimit = 0,
}: CreateAiChecklistCapabilityContextInput): AiChecklistCapabilityContext => {
  const blockedTodoPolicy = normalizeChecklistBlockedTodoPolicy(workspace)
  const subscriptionRestricted = isWorkspaceSubscriptionRestricted(workspace)
  const helperVisibilityDefaultsEnabled = Object.keys(workspace?.helperVisibilityMaskDefault ?? {}).length > 0
  return {
    plan: workspace?.plan ?? 'FREE_TIER',
    subscriptionStatus: workspace?.subscription?.status ?? 'UNKNOWN',
    subscriptionRestricted,
    attachmentContextLimit,
    attachmentIntelligenceAvailable: attachmentContextLimit !== 0 && !subscriptionRestricted,
    workspaceCurrency: workspace?.settings?.currency ?? 'USD',
    cardBackgroundsEnabled: workspace?.cardBackgroundSettings?.enabled !== false,
    stockModuleEnabled: isModuleEnabled(workspace, 'stock'),
    budgetModuleEnabled: isModuleEnabled(workspace, 'budget'),
    quotaModuleEnabled: isModuleEnabled(workspace, 'quota'),
    helperVisibilityDefaultsEnabled,
    blockedTodoPolicy,
  }
}

export const renderAiChecklistCapabilityContext = (
  context: AiChecklistCapabilityContext,
): string => {
  const lines = [
    '[CHECKLIST_CAPABILITIES]',
    `Workspace subscription: plan=${context.plan}; status=${context.subscriptionStatus}; restricted=${formatBool(context.subscriptionRestricted)}.`,
    `Attachment intelligence: available=${formatBool(context.attachmentIntelligenceAvailable)}; max context attachments per chat=${context.attachmentContextLimit === -1 ? 'unlimited' : String(context.attachmentContextLimit)}.`,
    `Checklist types supported by the app: INDIVIDUAL (one-off), DAILY (recurring with one completion state per calendar date), WEEKLY (recurring with one completion state per ISO week).`,
    `Recurring semantics: title stays global; most detail fields can be reused across occurrences; completion, proof, review, timer, and memo state are occurrence-specific.`,
    `Checklist fields you may use when the user explicitly asks for them or the attachments clearly support them:`,
    '- Core: text, description, completionCriteria, priority, weightage, checklistType.',
    '- Scheduling: scheduleMode, scheduleStartDate, scheduleEndDate, hasTimeWindow, scheduleTimeFrom, scheduleTimeTo.',
    '- Workflow and verification: workflowStatus, requiresProofFile, proofApproverId, proofApproverName, requiresCoSign, coSignerRoleTarget, requiresLocationPin, targetCoordinates.',
    `- Resources and limits: estimatedHours, actualCost, budgetLimit (${context.budgetModuleEnabled ? 'enabled' : 'disabled'}), estimatedCost (${context.budgetModuleEnabled ? 'enabled' : 'disabled'}), soldUnits (${context.quotaModuleEnabled ? 'enabled' : 'disabled'}), currencyCode=${context.workspaceCurrency}, requiredStockIds/stockRequirements/consumptionMode (${context.stockModuleEnabled ? 'enabled' : 'disabled'}).`,
    '- Visibility and attachments: assigneeId, isHelperVisible, helperVisibilityMask, attachedFormIds, attachedDesignIds, cardBackground fields.',
    '- Delay and timer controls: countdownHours, enforcePaintSpringDelay, delayDurationHours.',
    `Workspace checklist blocked-item policy: completion=${formatBool(context.blockedTodoPolicy.allowCompletion)}, proof=${formatBool(context.blockedTodoPolicy.allowProof)}, statusChange=${formatBool(context.blockedTodoPolicy.allowStatusChange)}, comment=${formatBool(context.blockedTodoPolicy.allowComment)}.`,
    `Workspace surfaces: card backgrounds enabled=${formatBool(context.cardBackgroundsEnabled)}; helper visibility defaults configured=${formatBool(context.helperVisibilityDefaultsEnabled)}.`,
    'Question discipline for Line:',
    '- Ask about checklistType only when the user wants repeating work and has not already implied daily, weekly, or one-off behavior.',
    '- Ask about proof, approver, co-sign, or location gates only for compliance, field work, approvals, or evidence-heavy workflows.',
    '- Ask about stock, budget, quota, or countdown rules only when the request or attachments mention inventory, cost, sales, capacity, time-boxes, or operational controls.',
    '- If the user says to choose, default to INDIVIDUAL, weightage 1, workflowStatus CREATED, and no proof/location/co-sign/stock/budget extras.',
    '- Do not ask again for any checklist detail that the thread state or attachment evidence already answered.',
    '[END_CHECKLIST_CAPABILITIES]',
  ]
  return lines.join('\n')
}
